<template>
  <form v-if="isInterview && !isOnlySubscription" @submit.prevent>
    <div class="card my-5">
      <div class="row align-items-center justify-content-between">
        <div class="card-title col-auto mb-3">
          <img
            src="~@/assets/img/ic-wallet.svg"
            alt="Futuro | Consultoria Financeira"
          />
          <h2>Pagamento do projeto financeiro (não inclui assinatura)</h2>
        </div>
        <spinner v-if="loading" class="mr-2" />
      </div>
      <div class="row px-3">
        <div class="col-md-6 col-12 my-3">
          <label for="config" class="form-label">
            Escolha a forma como o cliente irá pagar
          </label>
          <select
            id="config"
            v-model="config"
            class="form-select"
            @change="(event) => configurePayments(event)"
          >
            <option
              v-for="key in Object.keys(paymentConfigs)"
              :key="key"
              :value="key"
            >
              <span>
                {{ paymentConfigs[key] }}
              </span>
            </option>
          </select>
        </div>
      </div>
      <div v-if="totalValue !== transactionsTotalValue" class="row mt-3 px-3">
        <div class="col-12 totalizer red-container">
          <span
            >ATENÇÃO: as formas de pagamento não serão salvas enquanto houver
            divergência entre o total dos pagamentos e o valor total do
            investimento na consultoria</span
          >
        </div>
      </div>
      <div class="row justify-content-center px-3">
        <div
          v-for="(transactionConfiguration, index) in transactions"
          :key="`config-${index}`"
          class="inner-card mt-3"
          style="padding: 20px"
        >
          <div class="row mb-3">
            <div class="col-12">
              <h2>
                <span style="float: left">{{ index + 1 }}º pagamento</span>
              </h2>
            </div>
          </div>
          <div class="row justify-content-around">
            <div class="col-md-3 col-12">
              <label :for="`configAmount-${index}`" class="form-label">
                Valor a pagar
                {{
                  index === autoCalculatedTransactionIndex
                    ? "(calculado automaticamente)"
                    : ""
                }}
              </label>
              <input
                :id="`configAmount-${index}`"
                :ref="`amountInput-${index}`"
                v-model.lazy="transactionConfiguration.amount"
                v-money="moneyFormat"
                min="0"
                type="text"
                class="form-control"
                :class="{
                  'is-invalid':
                    (v$.transactions.$dirty &&
                      v$.transactions.$each.$response.$errors[index].amount
                        .length) ||
                    isMinimumEntryInvalid(transactionConfiguration) ||
                    isFirstCreditCardAmountInvalid(index),
                }"
                @keydown="blockNegativeAmount($event)"
                @blur="handleAmountBlur(index)"
              />
              <div class="invalid-feedback">
                <span v-if="isMinimumEntryInvalid(transactionConfiguration)">
                  {{ getMinimumEntryErrorMessage() }}
                </span>
                <span v-if="isFirstCreditCardAmountInvalid(index)">
                  {{ getCreditCardSplitErrorMessage() }}
                </span>
                <span
                  v-if="
                    v$.transactions.$dirty &&
                    v$.transactions.$each.$response.$errors[index].amount.length
                  "
                >
                  Digite um valor válido
                </span>
              </div>
            </div>
            <div class="col-md-3 col-12">
              <div class="row flex-column">
                <label class="form-label"> Forma de pagamento </label>
                <span class="mt-2">
                  <b>
                    {{
                      transactionPaymentTypes[
                        transactionConfiguration.paymentTypeId
                      ]
                    }}
                  </b>
                </span>
                <div class="invalid-feedback">
                  <span
                    v-if="
                      v$.transactions.$dirty &&
                      v$.transactions.$each.$response.$errors[index]
                        .paymentTypeId.length
                    "
                  >
                    Forma de pagamento não informada
                  </span>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-12">
              <div class="row flex-column">
                <label class="form-label"> Quantidade de parcelas </label>
                <span class="mt-2">
                  <b>
                    {{ transactionConfiguration.installments }}
                  </b>
                </span>
                <div class="invalid-feedback">
                  <span
                    v-if="
                      v$.transactions.$dirty &&
                      v$.transactions.$each.$response.$errors[index]
                        .installments.length
                    "
                  >
                    Não foi informada a quantidade de parcelas
                  </span>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-12">
              <div
                v-if="transactionConfiguration.installments > 1"
                class="row flex-column"
              >
                <label class="form-label"> Valor das parcelas </label>
                <span class="mt-2">
                  <b
                    >{{ transactionConfiguration.installments }}x de
                    {{
                      formatCurrency(
                        getInstallmentValue(transactionConfiguration),
                        "BRL"
                      )
                    }}
                  </b>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-3 px-3">
        <div
          class="col-12 totalizer"
          :class="{
            'green-container': totalValue === transactionsTotalValue,
            'red-container': totalValue !== transactionsTotalValue,
          }"
        >
          <span>Total dos pagamentos:</span>
          <span
            >{{ formatCurrency(transactionsTotalValue, "BRL") }}
            {{
              totalValue !== transactionsTotalValue
                ? getRemaingAmountMessage()
                : ""
            }}</span
          >
        </div>
      </div>
      <div v-if="totalValue" class="row my-3 px-3">
        <div class="col-12 blue-card totalizer investment-total-card">
          <span>Valor total do investimento no projeto financeiro:</span>
          <span>{{ formatCurrency(totalValue, "BRL") }}</span>
        </div>
      </div>
      <comments-section
        v-if="interviewSection && token"
        :interview-section="interviewSection"
        :token="token"
      />
    </div>
  </form>
</template>

<script>
import _ from "lodash";
import useVuelidate from "@vuelidate/core";
import { VMoney } from "v-money";
import {
  helpers,
  required,
  minValue,
  maxValue,
  minLength,
} from "@vuelidate/validators";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { floatToMoney, moneyToFloat } from "@/helpers/converters";
import { getTransactionConfigurationsPayload } from "@/helpers/interview_payloads";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import * as transactionPaymentTypes from "@/constants/transactionPaymentTypes";
import Spinner from "@/modules/shared/components/Spinner";
import CommentsSection from "./CommentsSection";
import { getBRLFormat } from "@/helpers/formatting";

export default {
  components: {
    Spinner,
    CommentsSection,
  },
  directives: {
    money: VMoney,
  },
  props: {
    interviewSection: {
      type: String,
      default: "",
    },
    isInterview: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["request-installment-investment"],
  setup(props) {
    return {
      v$: useVuelidate({ $registerAs: props.interviewSection }),
    };
  },
  data() {
    return {
      token: "",
      loading: false,
      deleting: -1,
      transactions: [],
      config: "1_0",
      tempPayload: null,
      lastSavedPayload: null,
      savePromise: null,
      paymentConfigs: {
        "1_0": "Pix (à vista)",
        "0_1": "Apenas um cartão de crédito",
        "1_1": "Entrada no Pix + Um cartão de crédito",
        "0_2": "Dois cartões de crédito",
        "1_2": "Entrada no Pix + Dois cartões de crédito",
        pix_installment: "Pix Parcelado",
      },
    };
  },
  validations() {
    return {
      transactions: {
        $each: helpers.forEach({
          id: {},
          paymentTypeId: { required },
          installments: {
            minValue: minValue(1),
            maxValue: maxValue(12),
          },
          amount: {
            minLength: minLength(7),
            greaterThanZero: (value) => {
              return moneyToFloat(value) > 0;
            },
            minimumEntryAmount: (value, transaction) => {
              return this.isMinimumEntryAmountValid({
                ...(transaction || {}),
                amount: value,
              });
            },
          },
        }),
      },
    };
  },
  computed: {
    ...mapGetters({
      proposal: types.PROPOSAL,
      calculation: types.PROPOSAL_CALCULATION,
      transactionPaymentTypes: sharedTypes.TRANSACTION_PAYMENT_TYPES,
      formatCurrency: types.FORMAT_CURRENCY,
    }),
    isOnlySubscription() {
      return this.proposal?.services_to_contract === 'only_subscription';
    },
    moneyFormat: function () {
      return getBRLFormat();
    },
    transactionInstallments() {
      return _.range(1, 13);
    },
    totalValue() {
      const key = ((this.proposal?.installment_quantity || 0) - 1).toString();
      if (
        this.calculation?.parcels_values &&
        key in this.calculation?.parcels_values
      ) {
        const total = this.calculation?.parcels_values[key].total_amount || 0;
        return parseFloat(total);
      }
      return 0;
    },
    transactionsTotalValue() {
      let total = 0;
      this.transactions.forEach((transaction) => {
        total += moneyToFloat(transaction.amount);
      });
      return parseFloat(total.toFixed(2));
    },
    minimumEntryAmount() {
      return parseFloat((this.totalValue * 0.1).toFixed(2));
    },
    creditCardTransactionPaymentType() {
      return transactionPaymentTypes.CREDIT_CARD.toString();
    },
    creditCardTransactionsCount() {
      return this.transactions.filter(
        (transaction) =>
          transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD
      ).length;
    },
    hasTwoCreditCardTransactions() {
      return this.creditCardTransactionsCount === 2;
    },
    firstCreditCardIndex() {
      if (!this.hasTwoCreditCardTransactions) return -1;
      return this.transactions.findIndex(
        (transaction) =>
          transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD
      );
    },
    autoCalculatedTransactionIndex() {
      if (!this.hasTwoCreditCardTransactions) return -1;
      return _.findLastIndex(
        this.transactions,
        (transaction) =>
          transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD
      );
    },
    nonCreditCardPaymentsTotal() {
      return _.sumBy(this.transactions, (transaction) =>
        transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD
          ? 0
          : moneyToFloat(transaction.amount)
      );
    },
    availableCreditCardTotal() {
      return parseFloat(
        (this.totalValue - this.nonCreditCardPaymentsTotal).toFixed(2)
      );
    },
    canSplitBetweenCreditCards() {
      // dois cartoes exigem no minimo um centavo para cada um
      return this.availableCreditCardTotal >= 0.02;
    },
  },
  watch: {
    // O watcher de calculation não detecta mudanças de parcelas (compara usando installment_quantity atual);
    // este watcher reage diretamente ao totalValue para sincronizar transações imediatamente.
    totalValue(newVal) {
      if (!this.transactions.length || !newVal) return;
      if (Math.abs(this.transactionsTotalValue - newVal) >= 0.01) {
        this.changePaymentInstallments(this.proposal?.installment_quantity || 1);
      }
    },
    proposal(newVal, oldVal) {
      const newInstallments = newVal?.installment_quantity;
      const oldInstallments = oldVal?.installment_quantity;

      if (
        !this.transactions.length ||
        !newInstallments ||
        !oldInstallments ||
        newInstallments === oldInstallments
      ) {
        return;
      }

      setTimeout(() => this.changePaymentInstallments(newInstallments), 2000);
    },
    calculation(newVal, oldVal) {
      const newTotal = this.getCalculationTotalValue(newVal);
      const oldTotal = this.getCalculationTotalValue(oldVal);

      if (
        !this.transactions.length ||
        !this.proposal?.installment_quantity ||
        !newTotal ||
        !oldTotal ||
        Math.abs(newTotal - oldTotal) < 0.01 ||
        Math.abs(this.transactionsTotalValue - newTotal) < 0.01
      ) {
        return;
      }

      setTimeout(() => this.syncTransactionAmountsToCurrentTotal(), 2000);
    },
  },
  async mounted() {
    this.token = this.$route.params.customerId;
    this.fetchAll();
  },
  methods: {
    ...mapActions({
      getTransactionPaymentTypes: sharedTypes.GET_TRANSACTION_PAYMENT_TYPES,
      getTransactionPayments: types.GET_TRANSACTION_PAYMENTS,
      autoSaveTransactionPayments: types.AUTO_SAVE_TRANSACTION_PAYMENTS,
      deleteTransactionPayment: types.DELETE_TRANSACTION_PAYMENT,
    }),
    ...mapMutations({
      mutateTransactionPayments: types.MUTATE_TRANSACTION_PAYMENTS,
      changeLoadingState: types.MUTATE_INTERVIEW_PROPOSAL_LOADING,
    }),
    async fetchAll() {
      this.changeLoadingState(true);
      try {
        await Promise.all([
          this.getTransactionPaymentTypes(),
          this.getTransactions(),
        ]);
      } catch (error) {
        this.$vfm.show("message", {
          type: "error",
          messages: [
            "Não foi possível obter as configurações do pagamento.",
            "Entre em contato com o time de TI antes de prosseguir com a operação.",
          ],
        });
      } finally {
        this.changeLoadingState(false);
      }
    },
    getTransactions() {
      return this.getTransactionPayments(this.token).then((response) => {
        this.fillTransactions(response.data?.transactions);
        const payload = getTransactionConfigurationsPayload(this);
        const payloadString = JSON.stringify(payload);
        this.mutateTransactionPayments(payload);
        this.tempPayload = payloadString;
        this.lastSavedPayload = payloadString;
        return Promise.resolve(response);
      });
    },
    getCalculationTotalValue(calculation) {
      const installments = (this.proposal?.installment_quantity || 1) - 1;
      const key = installments.toString();

      if (!calculation?.parcels_values || !(key in calculation.parcels_values)) {
        return 0;
      }

      return parseFloat(calculation.parcels_values[key].total_amount || 0);
    },
    fillTransactions(transactions) {
      let pixTransactionsQuantity = 0;
      let creditCardTransactionsQuantity = 0;
      let hasPixInstallmentTransaction = false;
      this.transactions = transactions.map((transaction) => {
        const { id, payment_type_id, installments, amount } = transaction;
        return {
          id,
          paymentTypeId: payment_type_id,
          installments,
          amount: floatToMoney(amount),
        };
      });
      this.transactions.forEach((transaction) => {
        if (transaction.paymentTypeId === transactionPaymentTypes.PIX) {
          pixTransactionsQuantity++;
        }
        if (transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD) {
          creditCardTransactionsQuantity++;
        }
        if (transaction.paymentTypeId === transactionPaymentTypes.PIX_INSTALLMENT) {
          hasPixInstallmentTransaction = true;
        }
      });
      // Pix Parcelado não entra na combinação pix+cartão de baixo — sem isso
      // o fallback (nenhum pix, nenhum cartão) apagava a transação e trocava
      // por um Pix à vista sozinho.
      if (hasPixInstallmentTransaction) {
        this.config = "pix_installment";
        this.fixTotalTransactionsValue();
        return;
      }
      if (
        (pixTransactionsQuantity === 0 &&
          creditCardTransactionsQuantity === 0) ||
        pixTransactionsQuantity > 1 ||
        creditCardTransactionsQuantity > 2
      ) {
        pixTransactionsQuantity = 1;
        creditCardTransactionsQuantity = 0;
        this.transactions = [
          {
            id: null,
            paymentTypeId: transactionPaymentTypes.PIX,
            installments: 1,
            amount: floatToMoney(this.totalValue),
          },
        ];
      }
      this.config = `${pixTransactionsQuantity}_${creditCardTransactionsQuantity}`;
      this.fixTotalTransactionsValue();
    },
    async handleAmountBlur(index) {
      const normalized = this.normalizeAmountSign(index);
      const recalculated = this.recalculateCreditCardBalance(index);
      if (normalized || recalculated) {
        const targetIndex = this.autoCalculatedTransactionIndex;
        await this.$nextTick();
        if (normalized) this.syncAmountInputValue(index);
        if (recalculated) this.syncAmountInputValue(targetIndex);
      }
      return this.preSave({ showValidationErrors: true });
    },
    blockNegativeAmount(event) {
      if (event.key === "-") event.preventDefault();
    },
    normalizeAmountSign(index) {
      const { amount } = this.transactions[index];
      // o v-money mantem o sinal no campo, mas moneyToFloat descarta: sem isso o negativo seria salvo como positivo.
      if (!amount || !amount.includes("-")) return false;

      this.transactions[index].amount = amount.replace(/-/g, "");
      return true;
    },
    recalculateCreditCardBalance(editedIndex) {
      const targetIndex = this.autoCalculatedTransactionIndex;
      if (targetIndex < 0 || targetIndex === editedIndex || !this.totalValue) {
        return false;
      }

      const otherTransactionsTotal = this.transactions.reduce(
        (total, transaction, index) =>
          index === targetIndex ? total : total + moneyToFloat(transaction.amount),
        0
      );
      const balance = Math.max(
        parseFloat((this.totalValue - otherTransactionsTotal).toFixed(2)),
        0
      );
      const newAmount = floatToMoney(balance);
      if (this.transactions[targetIndex].amount === newAmount) {
        return false;
      }

      this.transactions[targetIndex].amount = newAmount;
      return true;
    },
    syncAmountInputValue(index) {
      const inputRef = this.$refs[`amountInput-${index}`];
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
      if (!input) return;

      // v-model.lazy não escreve no DOM enquanto o input está focado; o v-money reformata e sincroniza.
      input.value = this.transactions[index].amount;
      input.dispatchEvent(new Event("input"));
    },
    isFirstCreditCardAmountInvalid(index) {
      if (!this.hasTwoCreditCardTransactions || index !== this.firstCreditCardIndex) {
        return false;
      }

      const amount = moneyToFloat(this.transactions[index].amount);
      return amount <= 0 || amount >= this.availableCreditCardTotal;
    },
    hasCreditCardSplitError() {
      return this.transactions.some((transaction, index) =>
        this.isFirstCreditCardAmountInvalid(index)
      );
    },
    getCreditCardSplitErrorMessage() {
      const available = this.formatCurrency(this.availableCreditCardTotal, "BRL");

      if (!this.nonCreditCardPaymentsTotal) {
        return `O valor do 1º cartão deve ser maior que zero e menor que ${available}, para sobrar saldo para o 2º cartão. Para cobrar tudo em um cartão, selecione "Apenas um cartão de crédito".`;
      }

      if (!this.canSplitBetweenCreditCards) {
        return "A entrada no Pix não deixa saldo para os cartões de crédito. Reduza a entrada ou escolha outra forma de pagamento.";
      }

      return `A soma dos dois cartões precisa ser ${available}, que é o total menos a entrada no Pix de ${this.formatCurrency(
        this.nonCreditCardPaymentsTotal,
        "BRL"
      )}. Reduza a entrada no Pix ou informe um valor menor no 1º cartão.`;
    },
    showCreditCardSplitError() {
      this.$vfm.show("message", {
        type: "error",
        messages: [this.getCreditCardSplitErrorMessage()],
      });
    },
    async preSave({ showValidationErrors = false } = {}) {
      const payload = getTransactionConfigurationsPayload(this);
      const currentPayload = JSON.stringify(payload);
      if (this.tempPayload === currentPayload) return true;

      this.tempPayload = currentPayload;
      this.mutateTransactionPayments(payload);
      if (this.hasMinimumEntryError()) {
        if (showValidationErrors) {
          this.showMinimumEntryError();
        }
        this.tempPayload = this.lastSavedPayload;
        return false;
      }

      if (this.hasCreditCardSplitError()) {
        if (showValidationErrors) {
          this.showCreditCardSplitError();
        }
        this.tempPayload = this.lastSavedPayload;
        return false;
      }

      const saved = await this.autoSave(payload);
      if (!saved) {
        this.tempPayload = this.lastSavedPayload;
      }

      return saved;
    },
    async autoSave(payload) {
      if (this.savePromise) {
        await this.savePromise;
        const refreshedPayload = getTransactionConfigurationsPayload(this);
        const refreshedPayloadString = JSON.stringify(refreshedPayload);
        if (this.lastSavedPayload === refreshedPayloadString) {
          return true;
        }
        payload = refreshedPayload;
      }

      const currentPayload = JSON.stringify(payload);
      if (this.lastSavedPayload === currentPayload) {
        this.tempPayload = currentPayload;
        return true;
      }

      const isFormCorrect = await this.v$.$validate();
      if (
        !isFormCorrect ||
        this.totalValue !== this.transactionsTotalValue ||
        this.hasMinimumEntryError()
      )
        return false;
      this.loading = true;
      this.savePromise = this.autoSaveTransactionPayments(payload)
        .then((response) => {
          this.fillTransactions(response.data?.transactions);
          const payload = getTransactionConfigurationsPayload(this);
          const payloadString = JSON.stringify(payload);
          this.mutateTransactionPayments(payload);
          this.tempPayload = payloadString;
          this.lastSavedPayload = payloadString;
          return true;
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            "Nao foi possivel salvar as formas de pagamento do projeto.";
          this.$vfm.show("message", {
            type: "error",
            messages: [
              message,
              "Entre em contato com o time de TI antes de prosseguir com a operacao.",
            ],
          });
          return false;
        })
        .finally(() => {
          this.loading = false;
          this.savePromise = null;
        });

      return this.savePromise;
    },
    async ensureSaved() {
      const payload = getTransactionConfigurationsPayload(this);
      this.mutateTransactionPayments(payload);

      if (this.hasMinimumEntryError()) {
        this.showMinimumEntryError();
        return false;
      }

      if (this.hasCreditCardSplitError()) {
        this.showCreditCardSplitError();
        return false;
      }

      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) {
        return false;
      }

      if (this.totalValue !== this.transactionsTotalValue) {
        this.$vfm.show("message", {
          type: "error",
          messages: [
            "As formas de pagamento do projeto precisam somar exatamente o valor total do investimento antes de gerar o contrato.",
          ],
        });
        return false;
      }

      if (this.savePromise) {
        await this.savePromise;
      }

      const refreshedPayload = getTransactionConfigurationsPayload(this);
      const refreshedPayloadString = JSON.stringify(refreshedPayload);
      if (this.lastSavedPayload === refreshedPayloadString) {
        this.tempPayload = refreshedPayloadString;
        return true;
      }

      this.tempPayload = refreshedPayloadString;
      const saved = await this.autoSave(refreshedPayload);
      if (!saved) {
        this.tempPayload = this.lastSavedPayload;
      }

      return saved;
    },
    getInstallmentValue(transactionConfiguration) {
      return (
        moneyToFloat(transactionConfiguration.amount) /
        parseInt(transactionConfiguration.installments || 1)
      );
    },
    getRemaingAmountMessage() {
      if (this.transactionsTotalValue > this.totalValue) {
        return "(valor diferente do total do investimento)";
      } else if (this.transactionsTotalValue < this.totalValue) {
        return `(adicione os ${this.formatCurrency(
          this.totalValue - this.transactionsTotalValue,
          "BRL"
        )} restantes)  `;
      }
      return "";
    },
    hasCreditCardTransaction() {
      return this.transactions.some(
        (transaction) =>
          transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD
      );
    },
    isPixTransaction(transaction) {
      return transaction?.paymentTypeId === transactionPaymentTypes.PIX;
    },
    isMinimumEntryAmountValid(transaction) {
      if (!this.isPixTransaction(transaction) || !this.hasCreditCardTransaction()) {
        return true;
      }

      return moneyToFloat(transaction.amount) >= this.minimumEntryAmount;
    },
    isMinimumEntryInvalid(transaction) {
      return !this.isMinimumEntryAmountValid(transaction);
    },
    hasMinimumEntryError() {
      return this.transactions.some((transaction) =>
        this.isMinimumEntryInvalid(transaction)
      );
    },
    getMinimumEntryErrorMessage() {
      return `O valor de entrada deve ser no minimo 10% do valor da EP (${this.formatCurrency(
        this.minimumEntryAmount,
        "BRL"
      )}).`;
    },
    showMinimumEntryError() {
      this.$vfm.show("message", {
        type: "error",
        messages: [this.getMinimumEntryErrorMessage()],
      });
    },
    configurePayments(event) {
      let newTransactions = [];
      if (event.target.value === "pix_installment") {
        // Sem parcelamento definido pelo cliente (a vista ou ainda nao
        // configurado), seleciona "Investimento parcelado" no Payment.vue com a
        // pre-configuracao dele. Isso persiste installment_quantity e recalcula
        // os descontos; a mutacao na store e sincrona, entao installment_quantity
        // e totalValue lidos abaixo ja refletem o parcelamento.
        if (!(this.proposal?.installment_quantity > 1)) {
          this.$emit("request-installment-investment");
        }
        newTransactions.push({
          id: null,
          paymentTypeId: transactionPaymentTypes.PIX_INSTALLMENT,
          installments: this.proposal?.installment_quantity || 1,
          amount: floatToMoney(this.totalValue),
        });
        this.transactions = newTransactions;
        this.fixTotalTransactionsValue();
        this.preSave();
        return;
      }
      const items = event.target.value.split("_");
      if (items.length === 2) {
        const pixTransactionsQuantity = parseInt(items[0] || "0");
        const creditCardTransactionsQuantity = parseInt(items[1] || "0");
        const totalQuantity =
          pixTransactionsQuantity + creditCardTransactionsQuantity;
        if (pixTransactionsQuantity) {
          for (let i = 0; i < pixTransactionsQuantity; i++) {
            newTransactions.push({
              id: null,
              paymentTypeId: transactionPaymentTypes.PIX,
              installments: 1,
              amount: floatToMoney(
                parseFloat((this.totalValue / totalQuantity).toFixed(2))
              ),
            });
          }
        }
        if (creditCardTransactionsQuantity) {
          for (let i = 0; i < creditCardTransactionsQuantity; i++) {
            newTransactions.push({
              id: null,
              paymentTypeId: transactionPaymentTypes.CREDIT_CARD,
              installments: this.proposal?.installment_quantity || 1,
              amount: floatToMoney(
                parseFloat((this.totalValue / totalQuantity).toFixed(2))
              ),
            });
          }
        }
        this.transactions = newTransactions;
        this.fixTotalTransactionsValue();
        this.preSave();
      }
    },
    fixTotalTransactionsValue() {
      const totalTransactions = _.sumBy(this.transactions, (transaction) =>
        moneyToFloat(transaction.amount)
      );
      if (this.totalValue && totalTransactions) {
        const difference = this.totalValue - totalTransactions;
        if (difference !== 0 && this.transactions.length > 0) {
          const targetIndex = Math.max(this.autoCalculatedTransactionIndex, 0);
          const newAmount = parseFloat(
            (moneyToFloat(this.transactions[targetIndex].amount) + difference).toFixed(2)
          );
          this.transactions[targetIndex].amount = floatToMoney(newAmount);
        }
      }
    },
    syncTransactionAmountsToCurrentTotal() {
      if (!this.transactions.length || !this.totalValue) return;

      const currentAmounts = this.transactions.map((transaction) =>
        moneyToFloat(transaction.amount)
      );
      const currentTotal = _.sum(currentAmounts);

      if (!currentTotal) {
        const evenAmount = parseFloat(
          (this.totalValue / this.transactions.length).toFixed(2)
        );
        this.transactions.forEach((transaction) => {
          transaction.amount = floatToMoney(evenAmount);
        });
      } else {
        let distributedTotal = 0;
        this.transactions.forEach((transaction, index) => {
          let amount = 0;

          if (index === this.transactions.length - 1) {
            amount = parseFloat((this.totalValue - distributedTotal).toFixed(2));
          } else {
            amount = parseFloat(
              ((currentAmounts[index] / currentTotal) * this.totalValue).toFixed(2)
            );
            distributedTotal += amount;
          }

          transaction.amount = floatToMoney(amount);
        });
      }

      this.fixTotalTransactionsValue();
      this.preSave();
    },
    changePaymentInstallments(installments) {
      const normalizedInstallments = parseInt(installments || 1);
      const hasPixTransaction = this.transactions.some(
        (transaction) => transaction.paymentTypeId === transactionPaymentTypes.PIX
      );
      const hasCreditCardTransaction = this.transactions.some(
        (transaction) =>
          transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD
      );
      const hasMixedEntry = hasPixTransaction && hasCreditCardTransaction;
      const hasPixInstallmentTransaction = this.transactions.some(
        (transaction) =>
          transaction.paymentTypeId === transactionPaymentTypes.PIX_INSTALLMENT
      );

      // Pix parcelado exige mais de uma parcela: ao voltar para "Investimento a
      // vista" no Payment.vue a transacao retorna para Pix a vista.
      if (normalizedInstallments <= 1 && hasPixInstallmentTransaction) {
        this.transactions.forEach((transaction) => {
          if (
            transaction.paymentTypeId === transactionPaymentTypes.PIX_INSTALLMENT
          ) {
            transaction.paymentTypeId = transactionPaymentTypes.PIX;
          }
        });
        this.config = "1_0";
      }

      this.transactions.forEach((transaction) => {
        if (
          normalizedInstallments > 1 &&
          transaction.paymentTypeId === transactionPaymentTypes.PIX &&
          !hasMixedEntry
        ) {
          transaction.paymentTypeId = transactionPaymentTypes.CREDIT_CARD;
        }
        transaction.installments =
          transaction.paymentTypeId === transactionPaymentTypes.CREDIT_CARD ||
          transaction.paymentTypeId === transactionPaymentTypes.PIX_INSTALLMENT
            ? normalizedInstallments
            : 1;
      });

      if (Math.abs(this.transactionsTotalValue - this.totalValue) >= 0.01) {
        this.syncTransactionAmountsToCurrentTotal();
        return;
      }

      this.preSave();
    },
  },
};
</script>

<style lang="scss" scoped>
.totalizer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 15px 20px;
  border-radius: 14px;
  &.red-container {
    background-image: var(--red-gradient);
  }
  &.green-container {
    background-image: var(--green-gradient);
  }
  &.investment-total-card {
  background-color: rgba(24, 29, 47, 0.7) !important;
  }
}
</style>
